import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  clearAccessLevelCookie,
  readAccessLevelCookie,
  writeAccessLevelCookie,
  type AccessFeature,
  type AccessLevel,
  hasFeature,
} from "./access-level";

type AccessLevelContextValue = {
  readonly level: AccessLevel | null;
  readonly ready: boolean;
  /** True when the profile modal should be visible (first visit or explicit switch). */
  readonly promptOpen: boolean;
  readonly setLevel: (level: AccessLevel) => void;
  readonly clearLevel: () => void;
  readonly openPrompt: () => void;
  /** Hide the modal until the next explicit switch (no cookie written). */
  readonly dismissPrompt: () => void;
  readonly can: (feature: AccessFeature) => boolean;
};

const AccessLevelContext = createContext<AccessLevelContextValue | null>(null);

export function AccessLevelProvider({ children }: { readonly children: ReactNode }) {
  const [level, setLevelState] = useState<AccessLevel | null>(() => readAccessLevelCookie());
  const [promptOpen, setPromptOpen] = useState(() => readAccessLevelCookie() == null);
  const [ready] = useState(true);

  const setLevel = useCallback((next: AccessLevel) => {
    writeAccessLevelCookie(next);
    setLevelState(next);
    setPromptOpen(false);
  }, []);

  const clearLevel = useCallback(() => {
    clearAccessLevelCookie();
    setLevelState(null);
    setPromptOpen(true);
  }, []);

  const openPrompt = useCallback(() => {
    setPromptOpen(true);
  }, []);

  const dismissPrompt = useCallback(() => {
    setPromptOpen(false);
  }, []);

  const value = useMemo<AccessLevelContextValue>(
    () => ({
      level,
      ready,
      promptOpen,
      setLevel,
      clearLevel,
      openPrompt,
      dismissPrompt,
      can: (feature) => hasFeature(level, feature),
    }),
    [level, ready, promptOpen, setLevel, clearLevel, openPrompt, dismissPrompt],
  );

  return <AccessLevelContext.Provider value={value}>{children}</AccessLevelContext.Provider>;
}

export function useAccessLevel(): AccessLevelContextValue {
  const ctx = useContext(AccessLevelContext);
  if (!ctx) {
    throw new Error("useAccessLevel must be used within AccessLevelProvider");
  }
  return ctx;
}
